/**
 * Level 2 apps: a short chain of existing tools working on the one shared project. Pure data and status logic;
 * the screen that shows them is components/ConnectedApp.tsx.
 */
import type { ProjectData } from "./types";

export interface AppStep {
  /** The tool shown for this step. */
  toolId: string;
  label: string;
  /** Short line under the label, from what is in the project. */
  note: (d: ProjectData) => string;
  /** Has this step got what it needs? A step without `ready` is always open. */
  ready?: (d: ProjectData) => boolean;
  /** What to do first when it is not ready. */
  needs?: string;
}

export interface ConnectedAppDef {
  id: string;
  name: string;
  steps: AppStep[];
  /** Level 3 products list what a person ends up with. */
  outputs?: string[];
}

const plural = (n: number, one: string, many = one + "s") => `${n} ${n === 1 ? one : many}`;
const guests = (d: ProjectData) => plural(d.guests.length, "guest");
const sessions = (d: ProjectData) => plural(d.sessions.length, "session");

export const CONNECTED_APPS: ConnectedAppDef[] = [
  {
    id: "badge-pass-maker",
    name: "Badge and Pass Maker",
    steps: [
      { toolId: "guest-list-tracker", label: "Guests", note: guests },
      { toolId: "name-badge-maker", label: "Badges and passes", note: (d) => (d.guests.length ? `${guests(d)} ready to print` : "Needs guests"), ready: (d) => d.guests.length > 0, needs: "Add your guests first." },
      { toolId: "qr-scanner", label: "Door check", note: (d) => (d.guests.length ? "Scan passes offline" : "Needs guests"), ready: (d) => d.guests.length > 0, needs: "Add your guests first." },
    ],
  },
  {
    id: "invite-calendar-maker",
    name: "Invite and Calendar Maker",
    steps: [
      { toolId: "guest-list-tracker", label: "Guests", note: guests },
      { toolId: "invitation-card-maker", label: "Invitation card", note: guests },
      { toolId: "calendar-invite-maker", label: "Calendar invite", note: (d) => (d.sessions.length ? `${sessions(d)} in your agenda` : "Type the event details") },
    ],
  },
  {
    id: "expense-capture-sheet",
    name: "Expense Capture Sheet",
    steps: [
      { toolId: "receipt-to-expense-sheet", label: "Capture receipts", note: (d) => plural(d.expenses.length, "expense") },
      { toolId: "event-budget-planner", label: "Check the budget", note: (d) => plural(d.budgets.length, "budget line") },
      { toolId: "event-profit-ledger", label: "Profit statement", note: (d) => plural(d.expenses.length, "expense"), ready: (d) => d.expenses.length > 0 || d.budgets.length > 0, needs: "Add an expense or a budget line first." },
    ],
  },
  {
    id: "agenda-cue-sheet-builder",
    name: "Agenda and Cue Sheet Builder",
    steps: [
      { toolId: "event-day-schedule", label: "Schedule", note: sessions },
      { toolId: "agenda-builder", label: "Printed agenda", note: sessions, ready: (d) => d.sessions.length > 0, needs: "Add sessions first." },
      { toolId: "run-of-show-cue-sheet", label: "Cue sheet", note: sessions, ready: (d) => d.sessions.length > 0, needs: "Add sessions first." },
      { toolId: "focus-stage-timer", label: "Stage timer", note: sessions },
    ],
  },
  {
    id: "quote-comparison-builder",
    name: "Quote Comparison Builder",
    steps: [
      { toolId: "vendor-quote-comparison", label: "Compare quotes", note: (d) => plural(d.quotes.length, "quote") },
      { toolId: "event-budget-planner", label: "Budget", note: (d) => plural(d.budgets.length, "budget line") },
      { toolId: "purchase-order-generator", label: "Purchase order", note: (d) => plural(d.quotes.filter((x) => x.status === "accepted").length, "accepted quote"), ready: (d) => d.quotes.some((x) => x.status === "accepted"), needs: "Accept a quote first." },
    ],
  },
  {
    id: "rooming-dietary-sheet",
    name: "Rooming and Dietary Sheet",
    steps: [
      { toolId: "guest-list-tracker", label: "Guests", note: guests },
      { toolId: "dietary-access-summary", label: "Dietary and access", note: (d) => plural(d.guests.filter((g) => g.dietary || g.access).length, "guest with needs", "guests with needs") },
      { toolId: "label-sheet-maker", label: "Labels", note: guests },
    ],
  },
];

const sessionsReady = { ready: (d: ProjectData) => d.sessions.length > 0, needs: "Add sessions in the schedule step first." };
const guestsReady = { ready: (d: ProjectData) => d.guests.length > 0, needs: "Add your guests first." };

/** Level 3 workflow products: a whole job from start to outputs, built from the same steps. */
export const WORKFLOW_PRODUCTS: ConnectedAppDef[] = [
  {
    id: "guest-manager",
    name: "Guest Manager",
    outputs: ["Guest list CSV", "Dietary and access summary", "Name badges with QR passes", "Offline door check", "Guest labels"],
    steps: [
      { toolId: "guest-list-tracker", label: "Guest list", note: guests },
      { toolId: "dietary-access-summary", label: "Needs", note: (d) => plural(d.guests.filter((g) => g.dietary || g.access).length, "guest with needs", "guests with needs"), ...guestsReady },
      { toolId: "name-badge-maker", label: "Badges", note: guests, ...guestsReady },
      { toolId: "label-sheet-maker", label: "Labels", note: guests },
      { toolId: "qr-scanner", label: "Door check", note: (d) => (d.guests.length ? "Scan passes offline" : "Needs guests"), ...guestsReady },
    ],
  },
  {
    id: "invitation-automator",
    name: "Invitation Automator",
    outputs: ["Invitation card", "Calendar invite file (.ics)", "Event web page", "Bulk QR codes"],
    steps: [
      { toolId: "guest-list-tracker", label: "Guests", note: guests },
      { toolId: "invitation-card-maker", label: "Invitation card", note: guests },
      { toolId: "calendar-invite-maker", label: "Calendar invite", note: (d) => (d.sessions.length ? `${sessions(d)} in your agenda` : "Type the event details") },
      { toolId: "event-landing-page-generator", label: "Event page", note: sessions },
      { toolId: "bulk-qr", label: "QR codes", note: guests },
    ],
  },
  {
    id: "programme-show-day-planner",
    name: "Programme and Show-Day Planner",
    outputs: ["Schedule", "Printed agenda", "Crew cue sheet", "Stage timer", "Event checklist", "Catering quantities"],
    steps: [
      { toolId: "event-day-schedule", label: "Schedule", note: sessions },
      { toolId: "agenda-builder", label: "Agenda", note: sessions, ...sessionsReady },
      { toolId: "run-of-show-cue-sheet", label: "Cue sheet", note: sessions, ...sessionsReady },
      { toolId: "event-checklist-planner", label: "Checklist", note: (d) => plural(d.tasks.length, "task") },
      { toolId: "catering-quantity-calculator", label: "Catering", note: guests },
      { toolId: "focus-stage-timer", label: "Stage timer", note: sessions },
    ],
  },
  {
    id: "profit-expense-manager",
    name: "Profit and Expense Manager",
    outputs: ["Budget plan", "Expense sheet from receipts", "Income by source", "Merchandise order", "Timesheets", "Profit statement with break-even"],
    steps: [
      { toolId: "event-budget-planner", label: "Budget", note: (d) => plural(d.budgets.length, "budget line") },
      { toolId: "receipt-to-expense-sheet", label: "Receipts", note: (d) => plural(d.expenses.length, "expense") },
      { toolId: "expense-tracker", label: "Income", note: (d) => plural(d.expenses.filter((e) => e.kind === "income").length, "income line") },
      { toolId: "merchandise-order-sheet", label: "Merchandise", note: (d) => (d.guests.length ? `For ${guests(d)}` : "Per-person items") },
      { toolId: "timesheet-generator", label: "Timesheets", note: () => "Crew hours" },
      { toolId: "event-profit-ledger", label: "Profit statement", note: (d) => plural(d.expenses.length, "expense"), ready: (d) => d.expenses.length > 0 || d.budgets.length > 0, needs: "Add an expense or a budget line first." },
    ],
  },
  {
    id: "event-report-generator",
    name: "Event Report Generator",
    outputs: ["Event closure report (PDF)", "Profit statement", "Dietary and access summary", "Attendee certificates"],
    steps: [
      { toolId: "event-profit-ledger", label: "Profit statement", note: (d) => plural(d.expenses.length, "expense") },
      { toolId: "dietary-access-summary", label: "Guest summary", note: guests },
      { toolId: "event-closure-report-maker", label: "Closure report", note: (d) => `${guests(d)}, ${plural(d.expenses.length, "expense")}` },
      { toolId: "certificate-maker", label: "Certificates", note: guests },
    ],
  },
];

export const WAVE2_PRODUCTS: ConnectedAppDef[] = [
  {
    id: "vendor-quote-manager",
    name: "Vendor Quote Manager",
    outputs: ["Side-by-side quote comparison", "Budget lines from accepted quotes", "Purchase orders", "Packing slips for deliveries"],
    steps: [
      { toolId: "vendor-quote-comparison", label: "Compare quotes", note: (d) => plural(d.quotes.length, "quote") },
      { toolId: "event-budget-planner", label: "Budget", note: (d) => plural(d.budgets.length, "budget line") },
      { toolId: "purchase-order-generator", label: "Purchase order", note: (d) => plural(d.quotes.filter((x) => x.status === "accepted").length, "accepted quote"), ready: (d) => d.quotes.some((x) => x.status === "accepted"), needs: "Accept a quote in the first step." },
      { toolId: "packing-slip-generator", label: "Delivery slip", note: () => "For deliveries" },
    ],
  },
  {
    id: "travel-planner",
    name: "Travel Planner",
    outputs: ["Arrivals and departures board", "Itinerary", "Time zone plan", "Travel budget", "Packing list"],
    steps: [
      { toolId: "arrivals-departures-board", label: "Arrivals and departures", note: (d) => plural(d.guests.filter((g) => g.arrival || g.departure).length, "traveller") },
      { toolId: "travel-itinerary-planner", label: "Itinerary", note: () => "Plan the trip" },
      { toolId: "timezone-converter", label: "Time zones", note: () => "Convert times" },
      { toolId: "travel-budget-planner", label: "Travel budget", note: () => "Estimate costs" },
      { toolId: "travel-packing-list", label: "Packing list", note: () => "What to bring" },
    ],
  },
  {
    id: "accommodation-manager",
    name: "Accommodation Manager",
    outputs: ["Rooming list", "Hotel block request", "Arrivals and departures board", "Room labels"],
    steps: [
      { toolId: "guest-list-tracker", label: "Guests", note: (d) => plural(d.guests.filter((g) => g.needsStay).length, "guest needing a stay", "guests needing a stay") },
      { toolId: "rooming-list-planner", label: "Rooming list", note: (d) => {
        const need = d.guests.filter((g) => g.needsStay && g.status !== "declined").length;
        return d.rooms.length || !need ? plural(d.rooms.length, "room booking") : `0 room bookings, ${plural(need, "guest")} need${need === 1 ? "s" : ""} a room: press Add rooms for guests who need a stay`;
      } },
      { toolId: "arrivals-departures-board", label: "Arrivals", note: (d) => plural(d.guests.filter((g) => g.arrival).length, "arrival") },
      { toolId: "label-sheet-maker", label: "Labels", note: guests },
    ],
  },
  {
    id: "seating-floor-planner",
    name: "Seating and Floor Planner",
    outputs: ["Seating plan by table", "Floor layout drawing", "Place card and table labels", "Seating CSV and PDF"],
    steps: [
      { toolId: "guest-list-tracker", label: "Guests", note: guests },
      { toolId: "seating-chart-planner", label: "Seating", note: (d) => `${plural(d.guests.filter((g) => g.status !== "declined" && g.table?.trim()).length, "guest")} seated`, ...guestsReady },
      { toolId: "booth-layout-planner", label: "Floor layout", note: () => "Draw the room" },
      { toolId: "label-sheet-maker", label: "Place cards", note: (d) => (d.guests.some((g) => g.table?.trim()) ? `${guests(d)}, table shown on each card` : guests(d)) },
    ],
  },
];

export const WAVE3_PRODUCTS: ConnectedAppDef[] = [
  {
    id: "booth-manager",
    name: "Booth Manager",
    outputs: ["Exhibitor and booth list with payments", "Floor layout drawing", "Visitor lead capture sheets", "Exhibitor badges"],
    steps: [
      { toolId: "exhibitor-booth-tracker", label: "Exhibitors", note: (d) => plural(d.exhibitors.length, "exhibitor") },
      { toolId: "booth-layout-planner", label: "Floor layout", note: (d) => plural(d.exhibitors.filter((x) => x.booth).length, "booth assigned", "booths assigned") },
      { toolId: "visitor-lead-capture-sheet", label: "Lead capture", note: () => "Sheets for exhibitors" },
      { toolId: "name-badge-maker", label: "Badges", note: guests },
    ],
  },
  {
    id: "marketing-materials-manager",
    name: "Marketing Materials Manager",
    outputs: ["Invitation card", "Flyer or brochure", "Banner or sign", "Event web page", "QR codes"],
    steps: [
      { toolId: "invitation-card-maker", label: "Invitation", note: guests },
      { toolId: "brochure-flyer-maker", label: "Flyer", note: () => "Print or share" },
      { toolId: "banner-sign-maker", label: "Banner and signs", note: () => "Venue signage" },
      { toolId: "event-landing-page-generator", label: "Event page", note: sessions },
      { toolId: "bulk-qr", label: "QR codes", note: guests },
    ],
  },
  {
    id: "giveaway-branding-manager",
    name: "Giveaway and Branding Manager",
    outputs: ["Fair prize draw with backups", "Merchandise order", "Certificates", "Labels"],
    steps: [
      { toolId: "giveaway-winner-picker", label: "Prize draw", note: guests },
      { toolId: "merchandise-order-sheet", label: "Merchandise", note: (d) => (d.guests.length ? `For ${guests(d)}` : "Per-person items") },
      { toolId: "certificate-maker", label: "Certificates", note: guests },
      { toolId: "label-sheet-maker", label: "Labels", note: guests },
    ],
  },
];

export const getWorkflowProduct = (id: string) => WORKFLOW_PRODUCTS.find((a) => a.id === id);

export const getConnectedApp = (id: string) => CONNECTED_APPS.find((a) => a.id === id) ?? WORKFLOW_PRODUCTS.find((a) => a.id === id) ?? WAVE2_PRODUCTS.find((a) => a.id === id) ?? WAVE3_PRODUCTS.find((a) => a.id === id);

export interface StepStatus { ready: boolean; note: string; hint: string | null }
export function stepStatus(step: AppStep, d: ProjectData): StepStatus {
  const ready = step.ready ? step.ready(d) : true;
  return { ready, note: step.note(d), hint: ready ? null : step.needs ?? null };
}

/** Index of the step to open first: the first one that is not ready is skipped past; start at the first ready step. */
export const firstOpenStep = (app: ConnectedAppDef, d: ProjectData): number => Math.max(0, app.steps.findIndex((s) => (s.ready ? s.ready(d) : true)));
